/**
 * Extract FAQ-style Q&A pairs from HTML or plain-text blog content.
 *
 * A "question" is a heading (h2/h3/h4) or a bold/strong line that ends with "?".
 * The answer is the text that follows, up to the next heading/question or end
 * of content. Works isomorphically (SSR + client) with no DOM parser.
 */

export type FaqPair = { question: string; answer: string };

const HEADING_TAG = /<(h[234])[^>]*>([\s\S]*?)<\/\1>/gi;
const STRONG_LINE = /<(?:p|div)[^>]*>\s*<(?:strong|b)[^>]*>([\s\S]*?)<\/(?:strong|b)>\s*<\/(?:p|div)>/gi;

function stripTags(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&#8217;|&rsquo;/gi, "\u2019")
    .replace(/&#8216;|&lsquo;/gi, "\u2018")
    .replace(/&#8220;|&ldquo;/gi, "\u201C")
    .replace(/&#8221;|&rdquo;/gi, "\u201D")
    .replace(/&quot;/gi, '"')
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function isQuestion(text: string): boolean {
  const t = text.trim();
  if (t.length < 8 || t.length > 240) return false;
  return t.endsWith("?");
}

/**
 * Extract FAQ pairs from a chunk of HTML (WordPress `bodyHtml`).
 * Uses heading offsets to slice the body between questions and answers.
 */
export function extractFaqsFromHtml(html: string, max = 15): FaqPair[] {
  if (!html) return [];
  const markers: { index: number; length: number; text: string; type: "heading" | "strong" }[] = [];

  HEADING_TAG.lastIndex = 0;
  for (let m; (m = HEADING_TAG.exec(html)); ) {
    const text = stripTags(m[2]);
    markers.push({ index: m.index, length: m[0].length, text, type: "heading" });
  }
  STRONG_LINE.lastIndex = 0;
  for (let m; (m = STRONG_LINE.exec(html)); ) {
    const text = stripTags(m[1]);
    if (!isQuestion(text)) continue;
    markers.push({ index: m.index, length: m[0].length, text, type: "strong" });
  }
  markers.sort((a, b) => a.index - b.index);

  const faqs: FaqPair[] = [];
  for (let i = 0; i < markers.length && faqs.length < max; i++) {
    const cur = markers[i];
    if (!isQuestion(cur.text)) continue;
    const nextIndex = markers[i + 1]?.index ?? html.length;
    const answerHtml = html.slice(cur.index + cur.length, nextIndex);
    const answer = stripTags(answerHtml);
    if (answer.length < 20) continue;
    faqs.push({
      question: cur.text,
      answer: answer.length > 900 ? `${answer.slice(0, 897)}...` : answer,
    });
  }
  return dedupeFaqs(faqs);
}

/**
 * Extract FAQ pairs from a sectioned post (heading + paragraphs).
 */
export function extractFaqsFromSections(
  sections: { heading?: string; paragraphs: string[] }[] | undefined,
  max = 15,
): FaqPair[] {
  if (!sections?.length) return [];
  const out: FaqPair[] = [];
  for (const s of sections) {
    if (!s.heading || !isQuestion(s.heading)) continue;
    const answer = s.paragraphs.join(" ").trim();
    if (answer.length < 20) continue;
    out.push({
      question: s.heading.trim(),
      answer: answer.length > 900 ? `${answer.slice(0, 897)}...` : answer,
    });
    if (out.length >= max) break;
  }
  return dedupeFaqs(out);
}

function dedupeFaqs(faqs: FaqPair[]): FaqPair[] {
  const seen = new Set<string>();
  const out: FaqPair[] = [];
  for (const f of faqs) {
    const k = f.question.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(f);
  }
  return out;
}

/**
 * Build schema.org FAQPage JSON-LD from Q&A pairs.
 */
export function buildFaqPageSchema(faqs: FaqPair[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}
